import {type RefObject, useCallback, useEffect, useMemo, useRef} from 'react'
import {useEvent, useEventListener} from 'expo'
import {createVideoPlayer, type VideoPlayer} from 'expo-video'

import {getMasterVolume, getMuted, subscribeMaster} from '#/lib/sounds'
import {IS_WEB} from '#/env'

/**
 * Media engine for the capsule player — the single owner of the active
 * <video> element (web) / expo-video player (native), the background
 * preload player, and every raw media operation (play/pause/seek/rate/
 * audio). The controller feeds identity data (which URI is active, what to
 * preload) and receives media events through the latest-ref `on*` callbacks:
 * the engine never knows capsule/mode policy and the controller never
 * touches the element directly.
 *
 * Ownership map (capsule player):
 *  - engine.ts                  media element/player + platform seam (this file)
 *  - controller.ts              feed/index/mode/navigation policy + actions
 *  - components/PageChrome.tsx  active-page chrome presentation
 *  - index.tsx                  thin shell: layout, pager, prompt/quiz, arrows
 */

type MediaEngineOpts = {
  /** at:// URI of the capsule that opened the screen (resume-seek target). */
  openingUri: string | null
  /** rkey fallback for the resume-seek match when openingUri is unknown. */
  openingRkey: string
  /** Resume position carried from the feed card (null = none). */
  pendingStartTime: number | null
  onProgress: (currentTime: number, duration: number) => void
  onEnded: () => void
  onPausedChange: (paused: boolean) => void
  /** The active media became (un)able to play — drives the loading overlay. */
  onReadyChange: (ready: boolean) => void
}

export type MediaEngine = {
  /** Holder for the ACTIVE web page's <video> element (passed to VideoItem). */
  webVideoRef: RefObject<HTMLVideoElement | null>
  /** Native expo-video player (passed to VideoItem on native). */
  player: VideoPlayer
  play: () => void
  pause: () => void
  isPaused: () => boolean
  isEnded: () => boolean
  /** Seek by a delta with boundary clamping; null when no element is live. */
  seekByDelta: (delta: number) => {currentTime: number; duration: number} | null
  /** Raw seek (resume position); never clamps beyond what the element does. */
  seekTo: (target: number) => void
  getDuration: () => number
  /** Long-press 1.5×: sets/restores the playback rate (no policy). */
  setLongPress: (active: boolean) => void
  /** Feed the web start-muted policy (scroll pages); ignored on native. */
  setPolicyMuted: (muted: boolean) => void
  /** Play CTA: lift the policy, apply the master audio state, start if needed. */
  unmuteAndPlay: () => void
  /** Which capsule URI the active element now represents (resume-seek). */
  setActiveUri: (uri: string | null) => void
  /** (web) (Re)bind progress/end/pause listeners to the active element. */
  syncWebElement: () => void
  /** (web) Unbind listeners from the active element. */
  unbindWebElement: () => void
  /** (native) Replace the player source and start playback. */
  replaceSource: (playlist: string | undefined) => void
  /** (native) Background-buffer the next capsule's playlist. */
  preloadNext: (playlist: string | null) => void
}

export function useMediaEngine(opts: MediaEngineOpts): MediaEngine {
  // Latest-ref: listeners bound once per element always see the current
  // controller callbacks (progress/end/pause policy lives in the controller).
  const optsRef = useRef(opts)
  useEffect(() => {
    optsRef.current = opts
  })

  // ── Media state (all element/player reads go through these refs) ─────────
  const webVideoRef = useRef<HTMLVideoElement | null>(null)
  const boundElementRef = useRef<HTMLVideoElement | null>(null)
  const webHandlersRef = useRef<{remove: () => void} | null>(null)
  const pendingSeekRef = useRef<number | null>(opts.pendingStartTime)
  const openingUriRef = useRef(opts.openingUri)
  const openingRkeyRef = useRef(opts.openingRkey)
  const activeUriRef = useRef<string | null>(null)
  const prevRateRef = useRef(1)
  const lastPlaylistRef = useRef<string | null>(null)
  const preloadLastRef = useRef<string | null>(null)
  const sourceLoadSubRef = useRef<{remove: () => void} | null>(null)
  // Web start-muted policy (scroll pages): while set, the element plays muted
  // even when the master state is unmuted. The master mute ALWAYS wins over
  // this policy; the policy only ever adds silence on top of the master state.
  const policyMutedRef = useRef(false)
  // Tracks a native player that reached the end (expo-video exposes no `ended`
  // property) so the controller's ended → replay branch runs on native too.
  const nativeEndedRef = useRef(false)

  // ── Players (native) ──────────────────────────────────────────────────────
  const player = useMemo(() => {
    // expo-video expects a string source on web; an empty source keeps the
    // native player inert until the first playlist is loaded.
    const p = createVideoPlayer(IS_WEB ? '' : null)
    p.loop = false
    return p
  }, [])

  // Native: background-buffer the next capsule's video so the next swipe
  // starts playing instantly (TikTok-style preload).
  const preloadPlayer = useMemo(() => {
    if (IS_WEB) return null
    const p = createVideoPlayer(null)
    p.loop = false
    p.muted = true
    return p
  }, [])

  useEffect(() => {
    if (IS_WEB) return
    return () => {
      sourceLoadSubRef.current?.remove()
      player.pause()
      player.release()
      preloadPlayer?.pause()
      preloadPlayer?.release()
    }
  }, [player, preloadPlayer])

  // Native: expo-video events
  useEventListener(player, 'playToEnd', () => {
    if (IS_WEB) return
    nativeEndedRef.current = true
    optsRef.current.onEnded()
  })

  useEventListener(player, 'timeUpdate', evt => {
    if (IS_WEB) return
    optsRef.current.onProgress(evt.currentTime, player.duration)
  })

  // Native readiness: 'readyToPlay' is the only status in which the video can
  // start; anything else (idle/loading/error) keeps the loading overlay up.
  useEventListener(player, 'statusChange', evt => {
    if (IS_WEB) return
    optsRef.current.onReadyChange(evt.status === 'readyToPlay')
  })

  const nativePlaying = useEvent(player, 'playingChange', {
    isPlaying: player.playing,
  })
  useEffect(() => {
    if (IS_WEB) return
    if (nativePlaying.isPlaying) nativeEndedRef.current = false
    optsRef.current.onPausedChange(!nativePlaying.isPlaying)
  }, [nativePlaying.isPlaying])

  // ── Audio: single source of truth is the shared master state in lib/sounds.
  // The user-facing control (and the quiz) write master mute/volume; this
  // engine subscribes and mirrors the result onto the active element/player —
  // and re-applies it whenever a new element/player binds (capsule change), so
  // a fresh video inherits the mute/volume the user set anywhere.
  const syncAudio = useCallback(() => {
    const muted = (IS_WEB && policyMutedRef.current) || getMuted()
    const volume = getMasterVolume()
    if (IS_WEB) {
      const video = webVideoRef.current
      if (!video) return
      video.muted = muted
      video.volume = volume
    } else {
      player.muted = muted
      player.volume = volume
    }
  }, [player])

  // Subscribe once for the engine's whole life: every master mute/volume
  // change from any surface lands on the currently bound element/player.
  useEffect(() => subscribeMaster(syncAudio), [syncAudio])

  const setPolicyMuted = useCallback(
    (muted: boolean) => {
      policyMutedRef.current = muted
      syncAudio()
    },
    [syncAudio],
  )

  // ── Raw media operations ──────────────────────────────────────────────────
  const seekTo = useCallback(
    (target: number) => {
      if (IS_WEB) {
        const video = webVideoRef.current
        if (!video) return
        try {
          video.currentTime = target
        } catch {
          // ignore — seek while not seekable yet; a later event retries
        }
      } else {
        // A seek off the end clears the native ended state (replay path).
        nativeEndedRef.current = false
        player.currentTime = target
      }
    },
    [player],
  )

  const getDuration = useCallback((): number => {
    return IS_WEB ? (webVideoRef.current?.duration ?? 0) : player.duration
  }, [player])

  // Seek once to the position where the grid card's preview had stopped. Only
  // applies to the originally opened capsule and only when metadata is loaded
  // (so a startTime past the clip's end is detected and dropped). Clears
  // itself when applied or when the mapping is impossible.
  const attemptResumeSeek = useCallback(() => {
    const target = pendingSeekRef.current
    if (target == null) return
    const activeUri = activeUriRef.current
    if (activeUri) {
      const openingUri = openingUriRef.current
      const matches =
        openingUri != null
          ? activeUri === openingUri
          : openingRkeyRef.current !== '' &&
            activeUri.endsWith(openingRkeyRef.current)
      if (!matches) {
        // Different capsule than the one that opened the screen — drop it.
        pendingSeekRef.current = null
        return
      }
    }
    const dur = getDuration()
    if (!Number.isFinite(dur) || dur <= 0) return // metadata not ready yet
    if (target < dur - 0.25) seekTo(target)
    // Applied (or invalid: startTime ≥ duration) — resume is a one-shot.
    pendingSeekRef.current = null
  }, [getDuration, seekTo])

  const play = useCallback(() => {
    if (IS_WEB) {
      void webVideoRef.current?.play().catch(() => {})
    } else {
      player.play()
    }
  }, [player])

  const pause = useCallback(() => {
    if (IS_WEB) {
      webVideoRef.current?.pause()
    } else {
      player.pause()
    }
  }, [player])

  const isPaused = useCallback(() => {
    return IS_WEB ? (webVideoRef.current?.paused ?? true) : !player.playing
  }, [player])

  // Web reads the element's `ended`; native tracks playToEnd in a ref so the
  // controller's ended → replay branch behaves identically on both platforms.
  const isEnded = useCallback(() => {
    return IS_WEB
      ? (webVideoRef.current?.ended ?? false)
      : nativeEndedRef.current
  }, [])

  const seekByDelta = useCallback(
    (delta: number): {currentTime: number; duration: number} | null => {
      const applySeek = (ct: number, dur: number) => {
        const hi =
          Number.isFinite(dur) && dur > 0 ? dur : Number.POSITIVE_INFINITY
        return Math.max(0, Math.min(hi, ct + delta))
      }
      if (IS_WEB) {
        const video = webVideoRef.current
        if (!video) return null
        const target = applySeek(video.currentTime, video.duration)
        try {
          video.currentTime = target
        } catch {
          return null
        }
        return {currentTime: video.currentTime, duration: video.duration}
      }
      const dur = player.duration
      const target = applySeek(player.currentTime, dur)
      player.currentTime = target
      return {currentTime: player.currentTime, duration: dur}
    },
    [player],
  )

  const setLongPress = useCallback(
    (active: boolean) => {
      if (IS_WEB) {
        const video = webVideoRef.current
        if (!video) return
        if (active) {
          prevRateRef.current = video.playbackRate
          video.playbackRate = 1.5
        } else {
          video.playbackRate = prevRateRef.current
        }
      } else {
        if (active) {
          prevRateRef.current = player.playbackRate
          player.playbackRate = 1.5
        } else {
          player.playbackRate = prevRateRef.current
        }
      }
    },
    [player],
  )

  // Play CTA: lift the start-muted policy and mirror the master audio state
  // onto the element, then start if it isn't already playing. The user's
  // global mute still wins — pressing Play never blasts audio over a mute.
  const unmuteAndPlay = useCallback(() => {
    policyMutedRef.current = false
    syncAudio()
    if (IS_WEB) {
      const video = webVideoRef.current
      if (!video) return
      if (video.paused || video.ended) {
        void video.play().catch(() => {})
      }
    } else {
      try {
        nativeEndedRef.current = false
        if (!player.playing) player.play()
      } catch {
        // ignore — autoplay may still be blocked
      }
    }
  }, [player, syncAudio])

  const setActiveUri = useCallback((uri: string | null) => {
    activeUriRef.current = uri
  }, [])

  // ── (web) Element binding ─────────────────────────────────────────────────
  // The ACTIVE page's <video> element. Rebinds only when the element changed
  // (page activation flips the holder ref), so progress/end/pause listeners
  // follow the active page without ever re-running the HLS source attach.
  const syncWebElement = useCallback(() => {
    if (!IS_WEB) return
    const video = webVideoRef.current
    if (video === boundElementRef.current) return
    webHandlersRef.current?.remove()
    boundElementRef.current = video
    if (!video) return
    const onTime = () =>
      optsRef.current.onProgress(video.currentTime, video.duration)
    const onEnded = () => {
      optsRef.current.onPausedChange(true)
      optsRef.current.onEnded()
    }
    const onPlay = () => {
      optsRef.current.onPausedChange(false)
      // Playing implies the media can play — drop the loading overlay.
      optsRef.current.onReadyChange(true)
      // Re-apply the correct audio state: VideoItem forces muted=true before
      // play() to satisfy browser autoplay policy, so we unmute (or honour a
      // global mute) here as soon as playback is confirmed active.
      syncAudio()
    }
    const onPause = () => optsRef.current.onPausedChange(true)
    const onReady = () => attemptResumeSeek()
    // Readiness for the loading overlay: 'waiting' (initial load or a mid-play
    // buffer stall) lifts the overlay up; 'canplay' lowers it again. A native
    // video error (e.g. Safari HLS stream failure) also drops the overlay so
    // the VideoItem's error UI becomes visible instead of spinning forever.
    const onWaiting = () => optsRef.current.onReadyChange(false)
    const onCanPlay = () => optsRef.current.onReadyChange(true)
    const onVideoElementError = () => optsRef.current.onReadyChange(true)
    video.addEventListener('timeupdate', onTime)
    video.addEventListener('durationchange', onTime)
    video.addEventListener('ended', onEnded)
    video.addEventListener('play', onPlay)
    video.addEventListener('pause', onPause)
    video.addEventListener('waiting', onWaiting)
    video.addEventListener('canplay', onCanPlay)
    video.addEventListener('error', onVideoElementError)
    video.addEventListener('loadedmetadata', onReady)
    video.addEventListener('durationchange', onReady)
    video.addEventListener('canplay', onReady)
    webHandlersRef.current = {
      remove: () => {
        video.removeEventListener('timeupdate', onTime)
        video.removeEventListener('durationchange', onTime)
        video.removeEventListener('ended', onEnded)
        video.removeEventListener('play', onPlay)
        video.removeEventListener('pause', onPause)
        video.removeEventListener('waiting', onWaiting)
        video.removeEventListener('canplay', onCanPlay)
        video.removeEventListener('error', onVideoElementError)
        video.removeEventListener('loadedmetadata', onReady)
        video.removeEventListener('durationchange', onReady)
        video.removeEventListener('canplay', onReady)
      },
    }
    // A freshly bound element reports its current readiness immediately — a
    // preloaded neighbor arrives already playable, a cold start arrives with
    // nothing loaded yet and shows the loading overlay.
    optsRef.current.onReadyChange(video.readyState >= 3)
    // A freshly bound element inherits the master audio state (the mute/volume
    // the user set anywhere) — the engine owns audio, so a new page never
    // starts at the element's default full volume.
    syncAudio()
    // Position continuity: the seek can only run once metadata (and thus the
    // duration, which guards against startTime past the clip's end) is known.
    if (video.readyState >= 1) attemptResumeSeek()
  }, [attemptResumeSeek, syncAudio])

  const unbindWebElement = useCallback(() => {
    if (!IS_WEB) return
    webHandlersRef.current?.remove()
    webHandlersRef.current = null
    boundElementRef.current = null
  }, [])

  // ── (native) Source replacement ───────────────────────────────────────────
  const replaceSource = useCallback(
    (playlist: string | undefined) => {
      if (IS_WEB || !playlist) return
      // Skip replace if this is the same video URL (e.g. transitioning from
      // directActiveVideo to feedActiveVideo for the same capsule).
      if (playlist === lastPlaylistRef.current) return
      lastPlaylistRef.current = playlist
      player.replace(playlist)
      player.loop = false
      nativeEndedRef.current = false
      // Inherit the master audio state instead of hard-resetting to
      // `muted=false, volume=1` (that used to discard the user's volume/mute
      // on every capsule change).
      syncAudio()
      player.play()
      // Position continuity (native): seek once metadata is loaded so the
      // duration is known (guards against startTime past the clip's end).
      attemptResumeSeek()
      sourceLoadSubRef.current?.remove()
      sourceLoadSubRef.current = player.addListener('sourceLoad', () => {
        attemptResumeSeek()
      })
    },
    [player, attemptResumeSeek, syncAudio],
  )

  const preloadNext = useCallback(
    (playlist: string | null) => {
      if (IS_WEB || !preloadPlayer || !playlist) return
      if (playlist === preloadLastRef.current) return
      preloadLastRef.current = playlist
      preloadPlayer.replace(playlist)
    },
    [preloadPlayer],
  )

  return useMemo(
    () => ({
      webVideoRef,
      player,
      play,
      pause,
      isPaused,
      isEnded,
      seekByDelta,
      seekTo,
      getDuration,
      setLongPress,
      setPolicyMuted,
      unmuteAndPlay,
      setActiveUri,
      syncWebElement,
      unbindWebElement,
      replaceSource,
      preloadNext,
    }),
    [
      player,
      play,
      pause,
      isPaused,
      isEnded,
      seekByDelta,
      seekTo,
      getDuration,
      setLongPress,
      setPolicyMuted,
      unmuteAndPlay,
      setActiveUri,
      syncWebElement,
      unbindWebElement,
      replaceSource,
      preloadNext,
    ],
  )
}
